// 人件費計算ユーティリティ（Airシフトの実労働時間 × スタッフマスタの時給／月給）
//
// 仕様（spec.md §6.6 / LaborPage と整合）:
// - 役員・正社員: monthlySalary を全店舗の勤務時間比で按分（働いた店舗ごと）
// - アルバイト・スポット: 各店舗の actualWorkMinutes / 60 × hourlyWage
// - 月給型スタッフがその月に勤務していなければ、その月の人件費は 0
// - Airメイトの「人件費（Airシフト連携）」日割りは使用しない（生のシフト × マスタ給与から再計算）

export interface StaffLite {
  staffKey: string;
  employmentType?: string | null;
  hourlyWage?: number | null;
  monthlySalary?: number | null;
}

export interface ShiftLite {
  storeId: string;
  date: string; // "YYYY-MM-DD"
  staffKey: string;
  actualWorkMinutes: number;
}

export interface LaborCostResult {
  total: number;
  byStore: Record<string, number>;
  byStaff: Record<string, number>;
}

const isMonthlyStaff = (s: StaffLite): boolean =>
  (s.employmentType === '正社員' || s.employmentType === '役員') &&
  s.monthlySalary != null &&
  s.monthlySalary > 0;

/**
 * 1 ヶ月分のシフトとスタッフから人件費を計算する。
 * 月給型スタッフは、その月の店舗別勤務時間比で按分。
 */
export const computeLaborCostForMonth = (
  staff: StaffLite[],
  shiftsInMonth: ShiftLite[],
  filterStoreIds?: string[],
): LaborCostResult => {
  // staffKey → { total, byStore }
  const minutesByStaff: Record<string, { total: number; byStore: Record<string, number> }> = {};
  for (const s of shiftsInMonth) {
    if (!minutesByStaff[s.staffKey]) {
      minutesByStaff[s.staffKey] = { total: 0, byStore: {} };
    }
    const m = minutesByStaff[s.staffKey];
    m.byStore[s.storeId] = (m.byStore[s.storeId] ?? 0) + s.actualWorkMinutes;
    m.total += s.actualWorkMinutes;
  }

  const result: LaborCostResult = { total: 0, byStore: {}, byStaff: {} };
  const staffMap = new Map(staff.map((st) => [st.staffKey, st]));

  for (const [staffKey, m] of Object.entries(minutesByStaff)) {
    const st = staffMap.get(staffKey);
    if (!st) continue;
    const monthly = isMonthlyStaff(st);

    let staffTotal = 0;
    for (const [storeId, storeMinutes] of Object.entries(m.byStore)) {
      if (filterStoreIds && !filterStoreIds.includes(storeId)) continue;
      let cost = 0;
      if (monthly) {
        // 月給按分: 当該店舗 minutes / 全店舗 minutes × 月給
        cost = (st.monthlySalary ?? 0) * (storeMinutes / m.total);
      } else if (st.hourlyWage != null) {
        cost = (storeMinutes / 60) * st.hourlyWage;
      }
      result.byStore[storeId] = (result.byStore[storeId] ?? 0) + cost;
      staffTotal += cost;
    }
    result.byStaff[staffKey] = staffTotal;
    result.total += staffTotal;
  }

  return result;
};

/**
 * 期間内（複数月）のシフトとスタッフから人件費を計算する。
 * 月単位で按分してから合算するため、月給型の按分が正確。
 */
export const computeLaborCostForPeriod = (
  staff: StaffLite[],
  shifts: ShiftLite[],
  yearMonths: string[],
  filterStoreIds?: string[],
): LaborCostResult => {
  const total: LaborCostResult = { total: 0, byStore: {}, byStaff: {} };
  for (const ym of yearMonths) {
    const monthShifts = shifts.filter((s) => s.date.startsWith(ym));
    const m = computeLaborCostForMonth(staff, monthShifts, filterStoreIds);
    total.total += m.total;
    for (const [k, v] of Object.entries(m.byStore)) {
      total.byStore[k] = (total.byStore[k] ?? 0) + v;
    }
    for (const [k, v] of Object.entries(m.byStaff)) {
      total.byStaff[k] = (total.byStaff[k] ?? 0) + v;
    }
  }
  return total;
};

/**
 * 任意の日付範囲（from-to）のシフトとスタッフから人件費を計算する。
 * 月単位で按分するため、跨月でも正確。
 */
export const computeLaborCostForDateRange = (
  staff: StaffLite[],
  shifts: ShiftLite[],
  fromDate: string,
  toDate: string,
  filterStoreIds?: string[],
): LaborCostResult => {
  const inRange = shifts.filter((s) => s.date >= fromDate && s.date <= toDate);
  const yms = Array.from(new Set(inRange.map((s) => s.date.slice(0, 7))));
  return computeLaborCostForPeriod(staff, inRange, yms, filterStoreIds);
};
